import type { ProductName } from '@atlassian/jira-shared-types/src/product-name.tsx';
import type { LicensedJiraProduct } from '@atlassian/jira-platform-react-hooks-use-is-admin-hub-ai-enabled/src/common/types.tsx';

const PRODUCT_NAME: { [key: string]: ProductName } = {
	CORE: 'core',
	JIRA: 'jira',
	SERVICEDESK: 'serviceDesk',
	SOFTWARE: 'software',
	PRODUCT_DISCOVERY: 'product-discovery',
	CUSTOMER_SERVICE: 'customer-service',
};

const ADMIN_HUB_AI_PRODUCT: { [key: string]: LicensedJiraProduct } = {
	JIRA_CORE: 'jira-core',
	JIRA_SERVICEDESK: 'jira-servicedesk',
	JIRA_CUSTOMER_SERVICE: 'jira-customer-service',
	JIRA_SOFTWARE: 'jira-software',
	JIRA_PRODUCT_DISCOVERY: 'jira-product-discovery',
};

const STRING_FIELDS = ['summary', 'duedate'];
const SINGLE_SELECT_FIELDS = ['assignee', 'priority'];
const MULTI_SELECT_FIELDS = ['components', 'fixVersions', 'versions'];
const ARRAY_FIELDS = ['labels'];

/**
 * Takes in product name and returns it to a licensed product.
 * Necessary since the admin hub AI endpoint has a different naming convention
 */
export const getProductForAdminHubAi = (product: ProductName): LicensedJiraProduct => {
	switch (product) {
		case PRODUCT_NAME.CORE:
			return ADMIN_HUB_AI_PRODUCT.JIRA_CORE;

		case PRODUCT_NAME.SERVICEDESK:
			return ADMIN_HUB_AI_PRODUCT.JIRA_SERVICEDESK;

		case PRODUCT_NAME.CUSTOMER_SERVICE:
			return ADMIN_HUB_AI_PRODUCT.JIRA_CUSTOMER_SERVICE;

		case PRODUCT_NAME.PRODUCT_DISCOVERY:
			return ADMIN_HUB_AI_PRODUCT.JIRA_PRODUCT_DISCOVERY;

		case PRODUCT_NAME.SOFTWARE:
		case PRODUCT_NAME.JIRA:
		default:
			return ADMIN_HUB_AI_PRODUCT.JIRA_SOFTWARE;
	}
};

const handleSingleSelect = (value: unknown): unknown => {
	if (value && typeof value === 'object' && 'id' in value) {
		return value.id;
	}
	if (value && typeof value === 'object' && 'accountId' in value) {
		return value.accountId;
	}
	return value;
};

const handleArray = (value: unknown[]): unknown[] => {
	return value.map((item) => {
		if (typeof item === 'string') {
			return item;
		}
		if (item && typeof item === 'object') {
			return handleSingleSelect(item);
		}
		return item;
	});
};

export const transformTheIssueDataToGICFormat = (
	inputObject: Record<string, unknown> | undefined,
): Record<string, unknown> => {
	const outputObject: Record<string, unknown> = {};

	if (!inputObject) {
		return outputObject;
	}

	// Iterate over the entries of the input object
	for (const [key, value] of Object.entries(inputObject)) {
		if (typeof value === 'string' && STRING_FIELDS.includes(key)) {
			outputObject[key] = value;
		} else if (
			Array.isArray(value) &&
			(MULTI_SELECT_FIELDS.includes(key) || ARRAY_FIELDS.includes(key))
		) {
			outputObject[key] = handleArray(value);
		} else if (typeof value === 'object' && SINGLE_SELECT_FIELDS.includes(key)) {
			outputObject[key] = handleSingleSelect(value);
		}
	}

	return outputObject;
};
